import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { endOfMonth, isPast, isSameMonth, isToday, isWithinInterval, startOfMonth } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io';
import { Calendar, DateObject } from 'react-multi-date-picker';
import 'react-multi-date-picker/styles/layouts/prime.css';
import { useTheme } from 'styled-components';
import { IDemandViewModel, IDemandProposalViewModal, ISolicitationViewModel } from 'viewModels';
import { Tooltip } from '@mui/material';
import { Info } from '@mui/icons-material';
import { ReactComponent as IconView } from '../../assets/icons/icon-view.svg';
import { Button, NavHeader } from '../../components';
import { FeedMyServices } from '../../components/FeedMyServices';
import { FeedProfileInformation } from '../../components/FeedProfileInformation';
import { FeedProfilePicture } from '../../components/FeedProfilePicture';
import { FeedPublicationTextArea } from '../../components/FeedPublication/FeedPublicationTextArea';
import { Publication } from '../../components/FeedPublication/Publication';
import { ModalDetailsService } from '../../components/Modals/ModalDetailsSolicitation';
import { usePosts } from '../../context/posts';
import { useSolicitation } from '../../context/solicitation';
import { ServiceStatus, TypeUser } from '../../types/enums';
import {
  CardSolicitationDemand,
  Commitments,
  Container,
  ContainerProfile,
  ContainerPublication,
  ContainerServices,
} from './styles';
import { useAuth } from '../../context';
import ModalFeedbackForm from '../../components/Modals/ModalFeedbackForm';

export function FeedPage() {
  const { posts } = usePosts();
  const { demands, solicitations, demandProposals } = useSolicitation();
  const theme = useTheme();
  const [value, setValue] = useState<Date | null>(new Date());
  const [isOpenModalFeedbackForm, setIsOpenModalFeedbackForm] = useState(false);
  const [displayModalDetailsEvents, setDisplayModalDetailsEvents] = useState(false);
  const [solicitationSelected, setSolicitationSelected] = useState<ISolicitationViewModel>();
  const [demandSelected, setDemandSelected] = useState<IDemandViewModel>();
  const [demandPropsalSelected, setDemandProposalSelected] = useState<IDemandProposalViewModal>();
  const ref = useRef<HTMLDivElement>(null);
  const row = useRef<HTMLTableRowElement>(null);
  const [showMore, setShowMore] = useState(false);
  const [datesWithAppointments, setDatesWithAppointments] = useState<string[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<DateObject>(new DateObject(new Date()));
  const { user } = useAuth();

  const createData = (
    hour: string,
    solicitationsPerHour: ISolicitationViewModel[],
    demandsPerHour: IDemandViewModel[],
    demandProposalsPerHour: IDemandProposalViewModal[],
  ) => {
    return { hour, solicitationsPerHour, demandsPerHour, demandProposalsPerHour };
  };

  const isSameDateAndHour = (date: Date, selectedDate: Date | null, hour: string): boolean => {
    if (!selectedDate) return false;

    const sameDate =
      date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }) ===
      selectedDate.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });

    const selectedHour = parseInt(hour.split(':')[0], 10); // ex: "07:00" => 7
    const dateHour = date.getHours();

    return sameDate && selectedHour === dateHour;
  };

  const handleReturnSolicitationsFiltered = (hour: string) => {
    const solicitationsFiltered = solicitations?.filter((s) =>
      isSameDateAndHour(new Date(s.dateLimit), value, hour),
    );
    return solicitationsFiltered;
  };

  const handleReturnDemandsFiltered = (hour: string) => {
    const demandsFiltered = demands?.filter((s) =>
      isSameDateAndHour(new Date(s.dateLimit), value, hour),
    );
    return demandsFiltered;
  };

  const handleReturnDemandProposalsFiltered = (hour: string) => {
    const demandProposalsFiltered = demandProposals?.filter((s) =>
      isSameDateAndHour(new Date(s.dateLimit), value, hour),
    );
    return demandProposalsFiltered;
  };

  const handleOpenModalFeedbackForm = () => setIsOpenModalFeedbackForm(true);

  const events = [
    createData(
      '00',
      handleReturnSolicitationsFiltered('00:00'),
      handleReturnDemandsFiltered('00:00'),
      handleReturnDemandProposalsFiltered('00:00'),
    ),
    createData(
      '01',
      handleReturnSolicitationsFiltered('01:00'),
      handleReturnDemandsFiltered('01:00'),
      handleReturnDemandProposalsFiltered('01:00'),
    ),
    createData(
      '02',
      handleReturnSolicitationsFiltered('02:00'),
      handleReturnDemandsFiltered('02:00'),
      handleReturnDemandProposalsFiltered('02:00'),
    ),
    createData(
      '03',
      handleReturnSolicitationsFiltered('03:00'),
      handleReturnDemandsFiltered('03:00'),
      handleReturnDemandProposalsFiltered('03:00'),
    ),
    createData(
      '04',
      handleReturnSolicitationsFiltered('04:00'),
      handleReturnDemandsFiltered('04:00'),
      handleReturnDemandProposalsFiltered('04:00'),
    ),
    createData(
      '05',
      handleReturnSolicitationsFiltered('05:00'),
      handleReturnDemandsFiltered('05:00'),
      handleReturnDemandProposalsFiltered('05:00'),
    ),
    createData(
      '06',
      handleReturnSolicitationsFiltered('06:00'),
      handleReturnDemandsFiltered('06:00'),
      handleReturnDemandProposalsFiltered('06:00'),
    ),
    createData(
      '07',
      handleReturnSolicitationsFiltered('07:00'),
      handleReturnDemandsFiltered('07:00'),
      handleReturnDemandProposalsFiltered('07:00'),
    ),
    createData(
      '08',
      handleReturnSolicitationsFiltered('08:00'),
      handleReturnDemandsFiltered('08:00'),
      handleReturnDemandProposalsFiltered('08:00'),
    ),
    createData(
      '09',
      handleReturnSolicitationsFiltered('09:00'),
      handleReturnDemandsFiltered('09:00'),
      handleReturnDemandProposalsFiltered('09:00'),
    ),
    createData(
      '10',
      handleReturnSolicitationsFiltered('10:00'),
      handleReturnDemandsFiltered('10:00'),
      handleReturnDemandProposalsFiltered('10:00'),
    ),
    createData(
      '11',
      handleReturnSolicitationsFiltered('11:00'),
      handleReturnDemandsFiltered('11:00'),
      handleReturnDemandProposalsFiltered('11:00'),
    ),
    createData(
      '12',
      handleReturnSolicitationsFiltered('12:00'),
      handleReturnDemandsFiltered('12:00'),
      handleReturnDemandProposalsFiltered('12:00'),
    ),
    createData(
      '13',
      handleReturnSolicitationsFiltered('13:00'),
      handleReturnDemandsFiltered('13:00'),
      handleReturnDemandProposalsFiltered('13:00'),
    ),
    createData(
      '14',
      handleReturnSolicitationsFiltered('14:00'),
      handleReturnDemandsFiltered('14:00'),
      handleReturnDemandProposalsFiltered('14:00'),
    ),
    createData(
      '15',
      handleReturnSolicitationsFiltered('15:00'),
      handleReturnDemandsFiltered('15:00'),
      handleReturnDemandProposalsFiltered('15:00'),
    ),
    createData(
      '16',
      handleReturnSolicitationsFiltered('16:00'),
      handleReturnDemandsFiltered('16:00'),
      handleReturnDemandProposalsFiltered('16:00'),
    ),
    createData(
      '17',
      handleReturnSolicitationsFiltered('17:00'),
      handleReturnDemandsFiltered('17:00'),
      handleReturnDemandProposalsFiltered('17:00'),
    ),
    createData(
      '18',
      handleReturnSolicitationsFiltered('18:00'),
      handleReturnDemandsFiltered('18:00'),
      handleReturnDemandProposalsFiltered('18:00'),
    ),
    createData(
      '19',
      handleReturnSolicitationsFiltered('19:00'),
      handleReturnDemandsFiltered('19:00'),
      handleReturnDemandProposalsFiltered('19:00'),
    ),
    createData(
      '20',
      handleReturnSolicitationsFiltered('20:00'),
      handleReturnDemandsFiltered('20:00'),
      handleReturnDemandProposalsFiltered('20:00'),
    ),
    createData(
      '21',
      handleReturnSolicitationsFiltered('21:00'),
      handleReturnDemandsFiltered('21:00'),
      handleReturnDemandProposalsFiltered('21:00'),
    ),
    createData(
      '22',
      handleReturnSolicitationsFiltered('22:00'),
      handleReturnDemandsFiltered('22:00'),
      handleReturnDemandProposalsFiltered('22:00'),
    ),
    createData(
      '23',
      handleReturnSolicitationsFiltered('23:00'),
      handleReturnDemandsFiltered('23:00'),
      handleReturnDemandProposalsFiltered('23:00'),
    ),
  ];

  useEffect(() => {
    if (ref.current) {
      if (row.current) {
        row.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, []);

  // Atribui todas as solicitações e demandas em uma única variável "appointments"
  const appointments = [...solicitations, ...demands];

  // Obtém todas as datas que têm compromissos no mês atual
  const getDatesWithAppointmentsForMonth = (date: DateObject) => {
    const monthStart = startOfMonth(date.toDate());
    const monthEnd = endOfMonth(date.toDate());

    //Filtra as datas que contém compromissos pelo mês selecionado
    const datesWithAppointmentsValue: string[] = appointments
      .filter((s) => isWithinInterval(new Date(s.dateLimit), { start: monthStart, end: monthEnd }))
      .map((s) => new Date(s.dateLimit).toLocaleDateString('pt-BR'));

    setDatesWithAppointments(datesWithAppointmentsValue);
  };

  // Função para tratar a mudança de mês e ano
  const handleMonthChange = (date: DateObject) => {
    setSelectedMonth(date);
    getDatesWithAppointmentsForMonth(date);
  };

  //Função para desabilitar o click dos dias que não fazem parte do mês selecionado (dias do início e final do mês que preenchem o espaço em branco)
  const mapDays = ({ date }: { date: DateObject }) => {
    // Verifica se o dia pertence ao mês selecionado
    const isDisabled = !isSameMonth(date.toDate(), selectedMonth.toDate());

    //Se o dia não pertence ao mês selecionado, desabilita o mesmo
    return {
      disabled: isDisabled,
      onClick: () => {},
    };
  };

  useEffect(() => {
    // Carregar os dias com compromissos ao montar o componente
    const currentDate = new DateObject(new Date());
    getDatesWithAppointmentsForMonth(currentDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitations]);

  useEffect(() => {
    // Aplicar a classe CSS 'has-appointment' aos dias com compromissos
    const applyHasAppointmentClass = () => {
      const days = document.querySelectorAll('.rmdp-day:not(.rmdp-deactive) span.sd'); //Pega os dias do mês selecionado no calendário

      days.forEach((day) => {
        //Percorre todos os dias
        const dayNumber = day?.textContent?.trim(); //Atribui o dia na variavel dayNumber

        if (dayNumber) {
          const hasAppointment = datesWithAppointments.some((appointment) => {
            const [appointmentDay] = appointment.split('/'); //Pega somente o dia da lista de datas que há compromissos
            return appointmentDay.replace(/^0+/, '') === dayNumber; //Retira o 0 à esquerda caso tenha, e retorna verdadeiro caso o dia do calendário seja igual à algum dia que há compromisso
          });

          if (hasAppointment) {
            //Se o dia tem compromisso atribui a class has-appointment para aplicar o css, caso não tenha remove
            day.parentElement?.classList.add('has-appointment');
          } else {
            day.parentElement?.classList.remove('has-appointment');
          }
        }
      });
    };

    applyHasAppointmentClass(); // Aplicar a classe ao montar o componente
  }, [datesWithAppointments, value]);

  return (
    <>
      <NavHeader typeHeader="company" />
      <Container>
        <ContainerProfile>
          <FeedProfilePicture />
          <div className="informationMobile">
            {!showMore && (
              <div className="showMore">
                <button type="button" onClick={() => setShowMore(true)}>
                  Exibir mais <IoIosArrowDown />
                </button>
              </div>
            )}
            {showMore && <FeedProfileInformation />}
            {showMore && (
              <div className="showMore">
                <button type="button" onClick={() => setShowMore(false)}>
                  Exibir menos <IoIosArrowUp />
                </button>
              </div>
            )}
          </div>
          <div className="information">
            <FeedProfileInformation />
            {/* <div className="containerSolicitation">
              <ButtonOpenSolicitation />
            </div> */}
          </div>
        </ContainerProfile>
        <ContainerPublication>
          <FeedPublicationTextArea />
          {posts.map((post) => (
            <Publication key={post.id} post={post} />
          ))}
        </ContainerPublication>
        <Commitments>
          {user.type !== TypeUser.COMPANY && (
            <div className="feedback">
              <button
                type="button"
                className="btn-feedback"
                onClick={() => handleOpenModalFeedbackForm()}>
                Feedback Do Sistema
              </button>
              <Tooltip
                disableInteractive
                data-testid="tooltipCDA"
                title={
                  <h1 style={{ fontSize: '1rem' }}>
                    Seu feedback nos ajuda a tornar o sistema mais intuitivo e eficiente. Avalie a
                    usabilidade do sistema clicando no botão ao lado.
                  </h1>
                }
                placement="top"
                arrow>
                <Info
                  sx={{ cursor: 'pointer', color: 'rgb(255, 146, 0)', flex: '1 1 auto' }}
                  style={{ fontSize: '1.25rem' }}
                />
              </Tooltip>
            </div>
          )}
          <h1>Agenda</h1>
          <div className="calendar-time">
            <Calendar
              className="rmdp-prime"
              onChange={(e) => {
                if (Array.isArray(e)) {
                  const first = e[0];
                  if (first instanceof Object && 'toDate' in first) {
                    setValue((first as DateObject).toDate());
                  } else {
                    setValue(null);
                  }
                } else {
                  if (e instanceof Object && 'toDate' in e) {
                    setValue((e as DateObject).toDate());
                  } else {
                    setValue(null);
                  }
                }
              }}
              value={value}
              months={[
                'Janeiro',
                'Fevereiro',
                'Março',
                'Abril',
                'Maio',
                'Junho',
                'Julho',
                'Agosto',
                'Setembro',
                'Outubro',
                'Novembro',
                'Dezembro',
              ]}
              weekDays={['D', 'S', 'T', 'Q', 'Q', 'S', 'S']}
              format="DD/MM/YYYY"
              showOtherDays
              zIndex={1}
              onMonthChange={handleMonthChange}
              onYearChange={handleMonthChange}
              mapDays={mapDays}
            />
            <TableContainer
              className="table"
              style={{ maxHeight: '285px', maxWidth: '22rem' }}
              ref={ref}>
              <Table>
                <TableBody>
                  {events.map((event) => (
                    <TableRow
                      ref={event.hour === new Date().getHours().toString() ? row : null}
                      key={event.hour}
                      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell className="hour">{event.hour}</TableCell>
                      <TableCell>
                        <div className="containerCard">
                          {event.solicitationsPerHour.map((solicitation) => {
                            const eventDate = new Date(solicitation.dateLimit!);
                            const isSameDay = isToday(eventDate);
                            const isPastDate = isPast(eventDate);
                            let insideTimeLimit = false;

                            if (isSameDay || isPastDate) {
                              insideTimeLimit = true; // Fora do prazo (data é o mesmo dia de hoje ou já passou)
                            } else {
                              insideTimeLimit = false; // Dentro do prazo (data ainda não passou)
                            }

                            return (
                              <CardSolicitationDemand
                                condition={insideTimeLimit}
                                key={solicitation.id}>
                                <div className="card-info">
                                  <span>{solicitation.correspondence?.name ?? '-'}</span>
                                </div>
                                <Button
                                  LeftComponent={IconView}
                                  text=""
                                  onClick={() => {
                                    setSolicitationSelected(solicitation);
                                    setDemandSelected(undefined);
                                    setDemandProposalSelected(undefined);
                                    setDisplayModalDetailsEvents(true);
                                  }}
                                  styleLeftComponent={{ fill: theme.colors.primary }}
                                  border={{ color: 'transparent' }}
                                  backgroundHoverColor="transparent"
                                  dimensions={{
                                    width: '2rem',
                                    height: '1rem',
                                  }}
                                />
                              </CardSolicitationDemand>
                            );
                          })}
                          {event.demandsPerHour.map((demand) => {
                            const eventDate = new Date(demand.dateLimit!);
                            const isSameDay = isToday(eventDate);
                            const isPastDate = isPast(eventDate);

                            let insideTimeLimit = false;

                            if (isSameDay || isPastDate) {
                              insideTimeLimit = true; // Fora do prazo (data é o mesmo dia de hoje ou já passou)
                            } else {
                              insideTimeLimit = false; // Dentro do prazo (data ainda não passou)
                            }

                            return (
                              <CardSolicitationDemand condition={insideTimeLimit} key={demand.id}>
                                <div className="card-info">
                                  <span>{demand.correspondence?.name ?? '-'}</span>
                                </div>
                                <Button
                                  LeftComponent={IconView}
                                  text=""
                                  onClick={() => {
                                    setDemandSelected(demand);
                                    setSolicitationSelected(undefined);
                                    setDemandProposalSelected(undefined);
                                    setDisplayModalDetailsEvents(true);
                                  }}
                                  border={{ color: 'transparent' }}
                                  styleLeftComponent={{ fill: theme.colors.primary }}
                                  backgroundHoverColor="transparent"
                                  dimensions={{
                                    width: '2rem',
                                    height: '1rem',
                                  }}
                                />
                              </CardSolicitationDemand>
                            );
                          })}
                          {event.demandProposalsPerHour.map((demandProposal) => (
                            <div className="cardDemandProposal" key={demandProposal.id}>
                              <div className="card-info">
                                <span>{demandProposal.correspondence.name}</span>
                              </div>
                              <Button
                                LeftComponent={IconView}
                                text=""
                                onClick={() => {
                                  setDemandProposalSelected(demandProposal);
                                  setDemandSelected(undefined);
                                  setSolicitationSelected(undefined);
                                  setDisplayModalDetailsEvents(true);
                                }}
                                border={{ color: 'transparent' }}
                                styleLeftComponent={{ fill: theme.colors.primary }}
                                backgroundHoverColor="transparent"
                                dimensions={{
                                  width: '2rem',
                                  height: '1rem',
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
          <ContainerServices>
            {demands.filter(
              (d) => d.status !== ServiceStatus.CONCLUED && d.status !== ServiceStatus.CANCELED,
            ).length > 0 && (
              <FeedMyServices
                type="demand"
                services={demands.filter(
                  (d) => d.status !== ServiceStatus.CONCLUED && d.status !== ServiceStatus.CANCELED,
                )}
              />
            )}
            {solicitations.filter(
              (s) => s.status !== ServiceStatus.CONCLUED && s.status !== ServiceStatus.CANCELED,
            ).length > 0 && (
              <FeedMyServices
                type="solicitation"
                services={solicitations.filter(
                  (s) => s.status !== ServiceStatus.CONCLUED && s.status !== ServiceStatus.CANCELED,
                )}
              />
            )}
          </ContainerServices>
        </Commitments>
      </Container>
      <ModalDetailsService
        solicitation={solicitationSelected}
        demand={demandSelected}
        demandProposal={demandPropsalSelected}
        isOpen={displayModalDetailsEvents}
        onRequestClose={() => setDisplayModalDetailsEvents(false)}
      />
      <ModalFeedbackForm
        isOpen={isOpenModalFeedbackForm}
        onRequestClose={() => setIsOpenModalFeedbackForm(false)}
      />
    </>
  );
}
